
import React, { useState, useEffect } from 'react';
import LoginView from './components/LoginView';
import Spinner from './components/Spinner';
import { useAuth } from './hooks/useAuth';
import MainApp from './MainApp';
import { isFirebaseConfigured } from './services/firebaseService';
import FirebaseNotConfiguredView from './components/FirebaseNotConfiguredView';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// PWA 설치 프롬프트 이벤트에 대한 타입 정의
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export type AppMode = 'suneung' | 'naesin';

const queryClient = new QueryClient();

const App: React.FC = () => {
  // 앱 시작 시 Firebase 설정이 올바르게 되었는지 먼저 확인합니다.
  // 설정이 안되어있으면, 설정 안내 화면을 보여줍니다.
  if (!isFirebaseConfigured().configured) {
    return <FirebaseNotConfiguredView />;
  }
  
  // 설정이 되어있으면, 기존 인증 로직을 진행합니다.
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster 
        position="top-right" 
        containerClassName="print:hidden"
        toastOptions={{
            style: {
                background: '#27272A',
                color: '#F4F4F5',
                border: '1px solid #3F3F46',
            },
            success: {
                iconTheme: {
                    primary: '#4ADE80',
                    secondary: '#18181B',
                },
            },
            error: {
                iconTheme: {
                    primary: '#F87171',
                    secondary: '#18181B',
                },
            },
        }}
      />
      <AuthenticatedApp />
    </QueryClientProvider>
  );
};

// 이 컴포넌트는 인증 및 PWA 관련 로직을 담당합니다.
const AuthenticatedApp: React.FC = () => {
  const { user, loading } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<AppMode>('suneung');

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // FIX: Corrected typo from BeforeInstallGpromptEvent to BeforeInstallPromptEvent.
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      alert(
        "앱을 설치하려면 브라우저의 메뉴를 이용해주세요.\n\n" +
        "• iOS (Safari): 하단의 '공유' 버튼을 누른 후 '홈 화면에 추가'를 선택하세요.\n" +
        "• Android (Chrome): 우측 상단 '메뉴(⋮)' 버튼을 누른 후 '앱 설치' 또는 '홈 화면에 추가'를 선택하세요."
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex items-center justify-center">
        <Spinner message="인증 상태 확인 중..." />
      </div>
    );
  }

  if (!user) {
    return <LoginView onInstallClick={handleInstallClick} canInstall={true} />;
  }
  
  return <MainApp selectedMode={mode} />;
}

export default App;
